import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Truck, Zap, ShieldCheck, Star, BadgeCheck, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, errMsg, mediaUrl } from "../lib/api";
import { useAuth, useCart } from "../context/Store";
import ProductCard from "../components/ProductCard";

function Stars({ value, size = "h-4 w-4", onPick = null, testidPrefix = "star" }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" disabled={!onPick} onClick={() => onPick?.(i)}
          data-testid={onPick ? `${testidPrefix}-${i}` : undefined}
          className={onPick ? "cursor-pointer" : "cursor-default"}>
          <Star className={`${size} ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-brand-ink/20"}`} />
        </button>
      ))}
    </div>
  );
}

function Reviews({ slug, user }) {
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/products/${slug}/reviews`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [slug]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/products/${slug}/reviews`, { rating, text });
      toast.success("Review published — thank you!");
      setText("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  return (
    <div className="mt-24" data-testid="reviews-section">
      <div className="flex flex-wrap items-center gap-6 mb-10">
        <h2 className="font-display text-3xl font-bold tracking-tight">Ratings & reviews</h2>
        {data.count > 0 && (
          <div className="flex items-center gap-3">
            <span className="font-display text-4xl font-bold" data-testid="avg-rating">{data.avg_rating}</span>
            <div>
              <Stars value={data.avg_rating} />
              <p className="text-xs text-brand-ink/40 mt-1">{data.count} verified review{data.count !== 1 ? "s" : ""}</p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-5">
          {data.reviews.length === 0 && (
            <div className="rounded-3xl border border-dashed border-brand-ink/15 py-14 text-center" data-testid="reviews-empty">
              <p className="text-brand-ink/40">No reviews yet — be the first verified buyer to share one.</p>
            </div>
          )}
          {data.reviews.map((r) => (
            <div key={r.id} className="rounded-3xl bg-brand-ink/[0.03] p-6" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full brand-gradient-bg text-white text-xs font-bold flex items-center justify-center">{r.name[0].toUpperCase()}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold flex items-center gap-2">{r.name}
                    {r.verified_purchase && <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600"><BadgeCheck className="h-3.5 w-3.5" /> VERIFIED BUYER</span>}
                  </p>
                  <p className="text-xs text-brand-ink/40">{new Date(r.created_at).toLocaleDateString("en-IN")}</p>
                </div>
                <Stars value={r.rating} size="h-3.5 w-3.5" />
              </div>
              <p className="mt-3 text-sm text-brand-ink/70 leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>

        <div>
          {data.can_review ? (
            <form onSubmit={submit} className="rounded-3xl border border-brand-ink/10 p-6" data-testid="review-form">
              <h3 className="font-display text-lg font-bold">Write a review</h3>
              <p className="text-xs text-brand-ink/40 mt-1">You bought this product — share your experience.</p>
              <div className="mt-4"><Stars value={rating} size="h-7 w-7" onPick={setRating} testidPrefix="rating-star" /></div>
              <textarea
                data-testid="review-text"
                required
                minLength={4}
                maxLength={600}
                rows={4}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="How was the quality, delivery, value for money?"
                className="mt-4 w-full rounded-2xl bg-brand-ink/5 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brand-cyan"
              />
              <button type="submit" disabled={busy} data-testid="review-submit"
                className="mt-4 w-full h-11 rounded-full brand-gradient-bg text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Publish review
              </button>
            </form>
          ) : (
            <div className="rounded-3xl bg-brand-ink/[0.03] p-6 text-sm text-brand-ink/50" data-testid="review-note">
              {user
                ? "Reviews open to verified buyers — purchase this product to share yours."
                : <span><Link to={`/auth?next=/product/${slug}`} className="text-brand-blue font-semibold">Sign in</Link> to review products you've bought.</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    setData(null);
    setQty(1);
    setActiveImage(0);
    api.get(`/products/${slug}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [slug]);

  if (!data) return <div className="pt-40 pb-40 text-center text-brand-ink/40">Loading...</div>;
  if (data.error) return <div className="pt-40 pb-40 text-center font-display text-2xl" data-testid="product-not-found">Product not found</div>;

  const { product: p, related } = data;
  const productImages = p.images?.length ? p.images : [""];

  const changeImage = (direction) => setActiveImage((current) => (current + direction + productImages.length) % productImages.length);

  const addToCart = async (goCheckout) => {
    if (!user) {
      navigate(`/auth?next=/product/${slug}`);
      return;
    }
    setBusy(true);
    try {
      await add(p.id, qty);
      if (goCheckout) navigate("/checkout");
      else toast.success(`${p.name} added to cart`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-28 md:pt-36 pb-24" data-testid="product-detail-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
          <div className="absolute -inset-8 brand-gradient-bg opacity-10 blur-3xl rounded-full" />
          <div className="relative flex flex-col gap-3 sm:flex-row">
            {productImages.length > 1 && <div className="order-2 flex gap-2 overflow-x-auto sm:order-1 sm:w-16 sm:shrink-0 sm:flex-col sm:overflow-y-auto">
              {productImages.map((image, index) => <button key={`${image}-thumb-${index}`} type="button" onClick={() => setActiveImage(index)} aria-label={`Show product image ${index + 1}`} aria-pressed={activeImage === index}
                className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition ${activeImage === index ? "border-brand-blue" : "border-brand-ink/10 opacity-70 hover:opacity-100"}`}>
                <img src={mediaUrl(image)} alt="" className="h-full w-full object-cover" />
              </button>)}
            </div>}
            <div className="relative order-1 aspect-[4/5] min-w-0 flex-1 overflow-hidden rounded-3xl bg-[#F4F4F5]" data-testid="product-gallery"
              onTouchStart={(event) => { touchStartX.current = event.touches[0].clientX; }}
              onTouchEnd={(event) => { if (touchStartX.current === null) return; const delta = event.changedTouches[0].clientX - touchStartX.current; if (Math.abs(delta) > 45 && productImages.length > 1) changeImage(delta < 0 ? 1 : -1); touchStartX.current = null; }}>
              <img src={mediaUrl(productImages[activeImage])} alt={`${p.name} image ${activeImage + 1}`} className="h-full w-full object-cover" data-testid="product-image" />
              {productImages.length > 1 && <>
                <button type="button" aria-label="Previous product image" onClick={() => changeImage(-1)} className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-ink shadow"><ChevronLeft className="h-5 w-5" /></button>
                <button type="button" aria-label="Next product image" onClick={() => changeImage(1)} className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-ink shadow"><ChevronRight className="h-5 w-5" /></button>
                <span className="absolute bottom-3 right-3 rounded-full bg-black/55 px-3 py-1 text-xs font-semibold text-white">{activeImage + 1} / {productImages.length}</span>
              </>}
            {p.activation_eligible && (
              <span className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white">
                <Zap className="h-3.5 w-3.5" /> Activates Partner ID
              </span>
            )}
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="flex flex-col">
          <p className="text-xs uppercase tracking-[0.25em] text-brand-blue font-semibold">{p.brand} · {p.category}</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3" data-testid="product-name">{p.name}</h1>
          <div className="mt-6 flex items-baseline gap-4">
            <span className="font-display text-4xl font-bold" data-testid="product-price">{fmt(p.price)}</span>
            {p.mrp > p.price && <span className="text-lg text-brand-ink/40 line-through">{fmt(p.mrp)}</span>}
            {p.mrp > p.price && (
              <span className="rounded-full bg-brand-cyan/15 text-brand-blue text-xs font-bold px-3 py-1">
                {Math.round((1 - p.price / p.mrp) * 100)}% OFF
              </span>
            )}
          </div>
          <p className="text-xs text-brand-ink/40 mt-2">Inclusive of all taxes ({p.tax_rate}% GST) · SKU {p.sku}</p>

          <p className="mt-8 text-brand-ink/70 leading-relaxed" data-testid="product-description">{p.description}</p>

          {p.activation_eligible && (
            <div className="mt-8 rounded-2xl bg-brand-navy text-white p-6">
              <p className="font-display font-bold text-lg flex items-center gap-2"><Zap className="h-5 w-5 text-brand-cyan" /> Partner Activation Product</p>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">
                Buying this kit activates your Vearn Partner ID, referral code and binary position.
                One kit per customer — your Partner ID is yours for life.
              </p>
            </div>
          )}

          <div className="mt-8 flex items-center gap-3 text-sm">
            {p.stock > 0 ? (
              <span className="rounded-full bg-emerald-100 text-emerald-800 px-4 py-1.5 font-semibold" data-testid="stock-badge">In stock ({p.stock})</span>
            ) : (
              <span className="rounded-full bg-rose-100 text-rose-800 px-4 py-1.5 font-semibold" data-testid="stock-badge">Out of stock</span>
            )}
            <span className="flex items-center gap-1.5 text-brand-ink/50"><Truck className="h-4 w-4" /> Delivery in 3–6 days</span>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center rounded-full border border-brand-ink/15">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="h-12 w-12 flex items-center justify-center hover:text-brand-blue"><Minus className="h-4 w-4" /></button>
              <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(Math.min(10, qty + 1))} className="h-12 w-12 flex items-center justify-center hover:text-brand-blue"><Plus className="h-4 w-4" /></button>
            </div>
            <button data-testid="add-to-cart-button" disabled={busy || p.stock < 1} onClick={() => addToCart(false)}
              className="flex-1 h-12 rounded-full border-2 border-brand-ink font-semibold text-sm flex items-center justify-center gap-2 hover:bg-brand-ink hover:text-white transition-colors disabled:opacity-40">
              <ShoppingBag className="h-4 w-4" /> Add to Cart
            </button>
          </div>
          <button data-testid="buy-now-button" disabled={busy || p.stock < 1} onClick={() => addToCart(true)}
            className="mt-4 h-13 py-4 rounded-full brand-gradient-bg text-white font-semibold text-sm shadow-[0_8px_30px_rgba(41,98,255,0.35)] hover:scale-[1.01] transition-transform disabled:opacity-40">
            Buy Now with Cashfree
          </button>
          <p className="mt-4 flex items-center gap-2 text-xs text-brand-ink/40">
            <ShieldCheck className="h-4 w-4 text-brand-cyan" /> Secure checkout · UPI, cards, net banking & wallets
          </p>
        </motion.div>
      </div>

      <Reviews slug={slug} user={user} />

      {related?.length > 0 && (
        <div className="mt-28">
          <h2 className="font-display text-3xl font-bold tracking-tight mb-10">You may also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </div>
      )}
      <div className="mt-16">
        <Link to="/shop" className="text-sm font-semibold text-brand-blue">← Back to shop</Link>
      </div>
    </div>
  );
}
